import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal } from 'react-native';
import { useLazyListAllFeatureFlagsQuery } from '@/store/api/admin/featureFlagsApi';
import { FeatureFlagAttributes } from '@/types/admin/featureFlags';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useFeatureFlagActions } from './hooks/useFeatureFlagActions';
import { FlagDetailsContent } from './components/FlagDetailsContent';

interface FeatureFlagsDetailsSidebarProps {
  selectedFeatureFlag?: FeatureFlagAttributes;
  onClose: () => void;
  isOrganizationView: boolean;
}

export const FeatureFlagsDetailsSidebar: React.FC<FeatureFlagsDetailsSidebarProps> = ({
  selectedFeatureFlag,
  onClose,
  isOrganizationView,
}) => {
  const [getFeatureFlags] = useLazyListAllFeatureFlagsQuery();
  const [localFlags, setLocalFlags] = useState(selectedFeatureFlag?.flags || {});

  // Use shared hook for flag actions
  const {
    editingKey,
    editValue,
    setEditValue,
    handleResetFlag,
    handleSubmitValue,
    startEditing,
    cancelEditing,
  } = useFeatureFlagActions({
    selectedFlag: selectedFeatureFlag,
    isOrganizationView,
    localFlags,
    setLocalFlags,
  });

  useEffect(() => {
    setLocalFlags(selectedFeatureFlag?.flags || {});
  }, [selectedFeatureFlag]);

  const handleClose = () => {
    getFeatureFlags();
    onClose();
  };

  if (!selectedFeatureFlag) return null;

  return (
    <Modal
      visible={!!selectedFeatureFlag}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={handleClose}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerContent}>
            {isOrganizationView ? (
              <Text style={styles.title}>{selectedFeatureFlag.organizationName}</Text>
            ) : (
              <>
                <Text style={styles.title}>{selectedFeatureFlag.propertyName}</Text>
                <Text style={styles.subtitle}>{selectedFeatureFlag.organizationName}</Text>
              </>
            )}
          </View>
          <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
            <SVG_COMPONENTS.X_CIRCLE
              width={24}
              height={24}
              color={ADMIN_COLORS.TEXT_SECONDARY}
            />
          </TouchableOpacity>
        </View>

        <View style={styles.content}>
          <FlagDetailsContent
            localFlags={localFlags}
            editingKey={editingKey}
            editValue={editValue}
            setEditValue={setEditValue}
            onSubmitValue={handleSubmitValue}
            onCancelEditing={cancelEditing}
            onStartEditing={startEditing}
            onResetFlag={handleResetFlag}
          />
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  headerContent: {
    flex: 1,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  subtitle: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.SECONDARY,
    marginTop: 4,
  },
  closeButton: {
    padding: 8,
  },
  content: {
    flex: 1,
    padding: 16,
  },
});

